import {
    CloseIcon,
    InfoOutlineIcon,
    SearchIcon
} from '@chakra-ui/icons';
import {
    Box,
    Card,
    CardBody,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    List,
    ListItem,
    SkeletonText,
    Text,
    Tooltip
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { FaReact } from 'react-icons/fa';
import { MdBlock } from 'react-icons/md';
import {
    useGetAdvertisableAppsQuery,
    useGetApplicationsQuery
} from 'store/campaigns/adsetFormApi';
  
  function AppStore({ adsAccountId, isSaving, adSetFormik, onAppChange, context = '' }) {
    const inputRef = useRef(null);
    const popupRef = useRef(null);
    const previousAppStoreRef = useRef(adSetFormik?.app_store);
  
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [appOptions, setAppOptions] = useState([]);
    const [selectedAppOptions, setSelectedAppOptions] = useState({});
  
    let matchingPromotedObject;

    if(context === 'update'){
      matchingPromotedObject = adSetFormik?.promoted_object;
    }else if(context === 'trackEdit'){
      matchingPromotedObject = adSetFormik?.promoted_object[0];
    }else{
      matchingPromotedObject = adSetFormik?.promoted_object?.find(
        (obj) => obj.adsAccountId === adsAccountId
      );
    }
    
    const { data: advertisableApps } = useGetAdvertisableAppsQuery(
      { adsAccountId },
      { skip: !adsAccountId }
    );
  
    const { data: matchedApplications, isLoading: isMatchedAppLoading } = useGetApplicationsQuery(
      {
        searchTerm: selectedAppOptions?.id,
        adsAccountId,
        app_store: adSetFormik?.app_store
      },
      { skip: !selectedAppOptions?.id }
    );

    useEffect(() => {
      if (previousAppStoreRef.current !== adSetFormik?.app_store) {
        previousAppStoreRef.current = adSetFormik?.app_store;
        setSelectedAppOptions({});
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setAppOptions([]);
        setShowPopup(false);
        onAppChange([]);
      }
    }, [adSetFormik?.app_store]);

    useEffect(() => {
      if (matchedApplications?.length > 0) {
        onAppChange([
          {
            id: matchedApplications[0].app_id,
            original_object_url: matchedApplications[0].url,
          }
        ]);
        setSelectedAppOptions({
          name: matchedApplications[0].name,
          id: matchedApplications[0].app_id,
          url: matchedApplications[0].url
        });
      }
    }, [matchedApplications]);
  
    useEffect(() => {
      if (searchTerm?.length === 0) {
        setDebouncedSearchTerm('');
        return;
      }
  
      const handler = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, 800);
  
      return () => {
        clearTimeout(handler);
      };
    }, [searchTerm]);
  
    useEffect(() => {
      const appStore = adSetFormik?.app_store;
      if (!advertisableApps || !Array.isArray(advertisableApps)) return;
  
      const storeFiltered = advertisableApps.filter((app) => {
        if (appStore === 'google_play') {
          return app.cpa_access?.google_play === false;
        } else if (appStore === 'itunes') {
          return app.cpa_access?.itunes === false;
        }
        return false;
      });
  
      const searchFiltered = debouncedSearchTerm
        ? storeFiltered.filter((app) => {
            const searchLower = debouncedSearchTerm.toLowerCase();
            return (
              app.name?.toLowerCase().includes(searchLower) ||
              app.id?.toString().includes(searchLower) ||
              app.original_object_url?.toLowerCase().includes(searchLower)
            );
          })
        : storeFiltered;
  
      const filteredApps = searchFiltered.filter(
        (app) => app.id !== matchingPromotedObject?.application_id
      );
  
      // Auto-select promoted object app only if not already selected
      if (
        matchingPromotedObject?.application_id &&
        !selectedAppOptions?.id
      ) {
        const matchedApp = advertisableApps.find(
          (app) => app.id === matchingPromotedObject.application_id
        );
        if (matchedApp) {
          setSelectedAppOptions({
            name: matchedApp.name,
            id: matchedApp.id,
            url: matchedApp.original_object_url,
          });
        }
      }
  
      setAppOptions(filteredApps);
  
      // Only show popup if user is searching and not auto-selected
      if (debouncedSearchTerm && !matchingPromotedObject?.application_id) {
        setShowPopup(filteredApps.length > 0);
      } else {
        setShowPopup(false);
      }
    }, [debouncedSearchTerm, advertisableApps, adSetFormik?.app_store]);
  
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          inputRef.current &&
          !inputRef.current.contains(event.target) &&
          popupRef.current &&
          !popupRef.current.contains(event.target)
        ) {
          setShowPopup(false);
          setAppOptions([]);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
  
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);
  
    const handleAppSelect = (app) => {
      setSelectedAppOptions(app);
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setShowPopup(false);
      setAppOptions([]);
    };
  
    const handleAppStoreRedirect = () => {
      if (selectedAppOptions?.url) {
        window.open(selectedAppOptions?.url, '_blank');
      }
    };
  
    return (
      <Flex direction="column" gap={1} w={'100%'}>
        <Flex align={'center'}>
          <Text fontWeight="bold">App name</Text>
          <Box>
            <Tooltip
              borderRadius={5}
              label={
                <Box fontSize="12px" p={'5px'}>
                  <Text>
                    You can advertise any app registered on Meta’s developer site.
                  </Text>
                </Box>
              }
              fontSize="md"
            >
              <InfoOutlineIcon pointerEvents="all" ml={2} />
            </Tooltip>
          </Box>
        </Flex>
  
        {isMatchedAppLoading ? (
          <Flex>
            <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
          </Flex>
        ) : (
          <Box position="relative">
            <Text fontSize={'12px'}>
              Choose the app you want to advertise.
            </Text>
            <Flex>
              <InputGroup>
                <InputLeftElement height={'32px'}>
                  <SearchIcon boxSize={4} color="gray.500" />
                </InputLeftElement>
                <Input
                  ref={inputRef}
                  value={
                    selectedAppOptions?.name
                      ? selectedAppOptions?.name
                      : searchTerm
                  }
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter app name, app ID or exact app store URL"
                  borderRadius={'md'}
                  size={'sm'}
                  isDisabled={context === 'update'}
                  onFocus={() => {
                    const appStore = adSetFormik?.app_store;
  
                    if (!advertisableApps || advertisableApps.length === 0 || !appStore) return;
  
                    // Prevent opening if already selected via promoted_object
                    if (selectedAppOptions?.id || matchingPromotedObject?.application_id) return;
  
                    if (!searchTerm?.trim()) {
                      const storeFiltered = advertisableApps.filter((app) => {
                        if (appStore === 'google_play') {
                          return app.cpa_access?.google_play === false;
                        } else if (appStore === 'itunes') {
                          return app.cpa_access?.itunes === false;
                        }
                        return false;
                      });
  
                      setAppOptions(storeFiltered);
                      setShowPopup(storeFiltered.length > 0);
                    }
                  }}
                />
                <InputRightElement height={'32px'} cursor={'pointer'}>
                  {context === 'update' ? null : 
                    (searchTerm || selectedAppOptions?.name) && (
                      <CloseIcon
                        boxSize={3}
                        color="gray.500"
                        onClick={() => {
                          setSearchTerm('');
                          setDebouncedSearchTerm('');
                          setSelectedAppOptions({});
                          setAppOptions([]);
                          setShowPopup(false);
                          onAppChange([]);
                        }}
                      />
                    )
                  }
                </InputRightElement>
              </InputGroup>
            </Flex>
  
            <Flex mt={2}>
              {appOptions.length > 0 && showPopup && (
                <List
                  ref={popupRef}
                  position="absolute"
                  top="100%"
                  width="100%"
                  border="1px solid #ccc"
                  backgroundColor="white"
                  zIndex="1000"
                  maxHeight="200px"
                  overflowY="auto"
                >
                  {appOptions.map((app) => (
                    <ListItem
                      key={app.id}
                      p={'5px'}
                      px={'10px'}
                      cursor="pointer"
                      _hover={{ backgroundColor: '#f0f0f0' }}
                      onClick={() => handleAppSelect(app)}
                    >
                      <Flex gap={2} alignItems={'center'}>
                        <Box boxSize="24px" display="flex" alignItems="center" justifyContent="center" bg={'gray'} borderRadius={'sm'}>
                          <FaReact size={20} color="#fff" />
                        </Box>
                        <Flex direction={'column'}>
                          <Text fontSize={'12px'}>{app.name}</Text>
                          <Text fontSize={'10px'}>{`App ID : ${app.id}`}</Text>
                        </Flex>
                      </Flex>
                    </ListItem>
                  ))}
                </List>
              )}
            </Flex>
  
            {!showPopup && !selectedAppOptions?.name ? (
              <Card
                bg={isSaving ? '#fff' : 'rgb(224,237,247)'}
                borderRadius="5px"
                p={2}
                borderLeft={isSaving ? '4px solid rgb(220,55,60)' : undefined}
              >
                <CardBody display="flex" alignItems="center" gap={2} p={0}>
                  {isSaving ? (
                    <MdBlock style={{ pointerEvents: 'all' }} color="rgb(220,55,60)" />
                  ) : (
                    <InfoOutlineIcon
                      style={{ pointerEvents: 'all', marginLeft: '4px' }}
                      color="rgb(9,120,190)"
                    />
                  )}
                  <Text fontSize="12px" fontWeight="bold">
                    Select the app that you want people to install and use.
                  </Text>
                </CardBody>
              </Card>
            ) : (
              <Text
                _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                fontWeight={'normal'}
                color={'inherit'}
                fontSize={'10px'}
                mt={-1}
                onClick={handleAppStoreRedirect}
              >
                View this app in your selected app store
              </Text>
            )}
          </Box>
        )}
      </Flex>
    );
  }
  
  export default AppStore;
  